import { motion } from "framer-motion";

import type { BlogSummary as SummaryData } from "@/lib/api";
import { cn } from "@/lib/formatters";
import { useCountUp } from "@/lib/useCountUp";

interface BlogSummaryProps {
  summary: SummaryData;
}

function fmtInt(n: number): string {
  return Math.round(n).toLocaleString("en-US");
}

export function BlogSummary({ summary }: BlogSummaryProps) {
  const cards = [
    { label: "posts", value: summary.total_posts, sub: `${summary.posts_with_perf} with gsc data`, hue: "cyan" as const },
    { label: "total clicks", value: summary.total_clicks, sub: summary.analytics_period ?? "—", hue: "success" as const },
    { label: "total impressions", value: summary.total_impressions, sub: summary.analytics_period ?? "—", hue: "violet" as const },
    { label: "categories", value: Object.keys(summary.by_category).length, sub: topCat(summary.by_category), hue: "magenta" as const },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {cards.map((c, i) => (
        <SummaryCard key={c.label} label={c.label} value={c.value} sub={c.sub} hue={c.hue} index={i} />
      ))}
    </div>
  );
}

function topCat(byCat: Record<string, number>): string {
  const entries = Object.entries(byCat).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return "—";
  return `top: ${entries[0][0]} (${entries[0][1]})`;
}

type Hue = "cyan" | "magenta" | "violet" | "success";

function SummaryCard({
  label,
  value,
  sub,
  hue,
  index,
}: {
  label: string;
  value: number;
  sub: string;
  hue: Hue;
  index: number;
}) {
  const animated = useCountUp(value);
  const numClass =
    hue === "magenta" ? "num-hero-magenta text-magenta"
    : hue === "success" ? "num-hero-success text-success"
    : hue === "cyan" ? "num-hero text-primary"
    : "num-hero text-violet";

  const ledClass =
    hue === "magenta" ? "bg-magenta"
    : hue === "success" ? "bg-success"
    : hue === "cyan" ? "bg-primary"
    : "bg-violet";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="surface surface-hoverable p-4 relative"
    >
      <span className={cn("absolute top-3 right-3 w-1.5 h-1.5 rounded-full animate-pulse-led", ledClass)} />
      <p className="path-chip">{label}</p>
      <p className={cn("text-3xl font-mono mt-2", numClass)}>{fmtInt(animated)}</p>
      <p className="mt-1 text-xs font-mono text-muted truncate">{sub}</p>
    </motion.div>
  );
}
